import axios from 'axios';
import { EoaMetricsInfoResponse, EoaMetricsQueryResponse, MetricSource } from '../interfaces/MetricsInterface';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export const METRIC_TAG_PREFIX = 'xqmetric://';

const metricOptionValue = (source: MetricSource, name: string): string => `${source}:${name}`;

const splitMetricValue = (value: string): { source: MetricSource; name: string } => {
  const separator = value.indexOf(':');
  return { source: value.slice(0, separator) === 'survey' ? 'survey' : 'data', name: value.slice(separator + 1) };
};

const isMetricTag = (value: string): boolean => value.startsWith(METRIC_TAG_PREFIX);

const parseMetricTag = (tag: string): { source: MetricSource; name: string } | null => {
  if (!isMetricTag(tag)) return null;
  const rest = tag.slice(METRIC_TAG_PREFIX.length);
  const slash = rest.indexOf('/');
  if (slash <= 0) return null;
  const source = rest.slice(0, slash);
  const name = rest.slice(slash + 1);
  if (!name || name.includes('/')) return null;
  if (source !== 'data' && source !== 'survey') return null;
  return { source, name };
};

const getMetricsInfo = async (eoaId: string, accessToken: string): Promise<EoaMetricsInfoResponse> => {
  const response = await axios.get<EoaMetricsInfoResponse>(`${BASE_URL}/eoa/${eoaId}/metrics/info`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

const getSurveyMetricsInfo = async (eoaId: string, accessToken: string): Promise<EoaMetricsInfoResponse> => {
  const response = await axios.get<EoaMetricsInfoResponse>(`${BASE_URL}/eoa/${eoaId}/survey-metrics/info`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

const postMetricsQuery = async (
  eoaId: string,
  accessToken: string,
  metrics: string[]
): Promise<EoaMetricsQueryResponse> => {
  const response = await axios.post<EoaMetricsQueryResponse>(
    `${BASE_URL}/eoa/${eoaId}/metrics/query`,
    { metrics },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
};

const postSchoolMetricsQuery = async (
  eoaId: string,
  schoolId: string,
  accessToken: string,
  metrics: string[]
): Promise<EoaMetricsQueryResponse> => {
  const response = await axios.post<EoaMetricsQueryResponse>(
    `${BASE_URL}/eoa/${eoaId}/metrics/${schoolId}/query`,
    { metrics },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
};

const postSurveyMetricsQuery = async (
  eoaId: string,
  accessToken: string,
  metrics: string[]
): Promise<EoaMetricsQueryResponse> => {
  const response = await axios.post<EoaMetricsQueryResponse>(
    `${BASE_URL}/eoa/${eoaId}/survey-metrics/query`,
    { metrics },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
};

const postSchoolSurveyMetricsQuery = async (
  eoaId: string,
  schoolId: string,
  accessToken: string,
  metrics: string[]
): Promise<EoaMetricsQueryResponse> => {
  const response = await axios.post<EoaMetricsQueryResponse>(
    `${BASE_URL}/eoa/${eoaId}/survey-metrics/${schoolId}/query`,
    { metrics },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
};

export {
  getMetricsInfo,
  getSurveyMetricsInfo,
  postMetricsQuery,
  postSchoolMetricsQuery,
  postSurveyMetricsQuery,
  postSchoolSurveyMetricsQuery,
  metricOptionValue,
  splitMetricValue,
  isMetricTag,
  parseMetricTag,
};
